// Codes at the kiosk: checking a discount code and redeeming a coupon from the PC (F-POS-02, F-POS-06;
// spec/06 §8.4, §8.5; spec/12 §7.5, §8).
//
// Until now a code could be entered only in the console (`applyDiscountCode`, `redeemCoupon`). These are
// their station-authenticated twins, for the player on the station's own session — the station never names
// a player:
//
//   POST /v1/station/discount-codes/check   what a discount code takes off an amount — a quote, nothing
//                                           reserved. The kiosk shows the discount line and the new total
//                                           before paying; the code is applied for real (a use reserved,
//                                           audited) on the sale it pays: `discountCode` on
//                                           `purchaseStationPass`, and on `placeStationFoodOrder`.
//   POST /v1/station/coupons/redeem         redeem a coupon for the session's registered player — the same
//                                           redemption, ledger grant and audit as the console's.
//   POST /v1/station/quote                  what a pass or a food cart costs with the automatic promotions (and
//                                           a code) before paying — the same pricing the purchase runs, nothing
//                                           written (F-POS-03, F-KIO-14, F-KIO-10).
//
// A refusal names its reason (`discount-code-expired`, `coupon-used-up`, …) so the kiosk says it in words.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { PassProductIdSchema } from "../ids/schema.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { SaleQuoteSchema } from "./commerce.js";
import { CouponRedemptionSchema } from "./coupons.js";
import { PlaceFoodOrderLineSchema } from "./food.js";
const STATION_CODES_AUTH = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its own ' +
    'organisation, venue and station from the token; the player is the one on its own active session.';
/** A discount code as typed at the kiosk: the console's bounds; matched case-insensitively. */
export const StationDiscountCodeSchema = z
    .string()
    .min(4)
    .max(24)
    .openapi('StationDiscountCode', { description: 'A discount code as entered at the kiosk (F-POS-02); case does not matter.' });
/** Check a discount code against an amount (the cart's or the pass's total). */
export const CheckStationDiscountCodeSchema = z
    .object({
    code: StationDiscountCodeSchema,
    /** What the code would come off: the cart's inclusive total, or the pass's price. */
    amount: NonNegativeMoneySchema,
})
    .strict()
    .openapi('CheckStationDiscountCodeRequest', { description: 'Check a discount code at the kiosk (F-POS-02).' });
/**
 * What a discount code takes off an amount (F-POS-02, spec/06 §8.4): the same arithmetic the sale applies
 * (`discountAmount`, half-up once, never below zero). A quote: no use of the code is reserved until the code is
 * applied to the sale that pays.
 */
export const StationDiscountQuoteSchema = z
    .object({
    /** The code, upper-cased as it is stored. */
    code: z.string(),
    subtotal: MoneySchema,
    discount: MoneySchema,
    total: MoneySchema,
})
    .openapi('StationDiscountQuote', { description: 'What a discount code takes off at the kiosk (F-POS-02).' });
/**
 * Redeem a coupon at the kiosk. Looser than `CouponCode`: the server upper-cases it and reads the letters the
 * alphabet leaves out as the digits they look like (O → 0, I and L → 1), so a misread code still redeems.
 */
export const RedeemStationCouponSchema = z
    .object({ code: z.string().min(1).max(32) })
    .strict()
    .openapi('RedeemStationCouponRequest', { description: 'Redeem a coupon from the kiosk (F-POS-06).' });
/**
 * Price what the player is about to pay for at the kiosk (F-POS-03): **either** a pass (`passProductId`, the pass
 * sheet) **or** a food cart (`lines`, the Food screen), optionally with a discount code. The server prices it the way
 * the purchase will — the pass's live version on this station's shelf, the venue's menu — with the automatic
 * promotions for a kiosk sale by this session's player, in the venue's local time.
 */
export const StationQuoteSchema = z
    .object({
    passProductId: PassProductIdSchema.optional(),
    lines: z.array(PlaceFoodOrderLineSchema).min(1).max(200).optional(),
    discountCode: StationDiscountCodeSchema.optional(),
})
    .strict()
    .refine((body) => (body.passProductId === undefined) !== (body.lines === undefined), {
    message: 'Send either passProductId or lines.',
})
    .openapi('StationQuoteRequest', { description: 'Price a pass or a food cart at the kiosk before paying (F-POS-03).' });
export const quoteStationSaleRoute = createRoute({
    method: 'post',
    path: '/v1/station/quote',
    operationId: 'quoteStationSale',
    tags: ['commerce', 'stations'],
    summary: 'Price a pass or a food cart at the kiosk, promotions included (F-POS-03)',
    description: `${STATION_CODES_AUTH} Answers what the pass or the cart costs now: each line before any discount, the automatic ` +
        'promotions that apply (a happy hour, a first-order offer) with what each takes off, the code when one is sent, ' +
        'and the total the purchase will charge. The same pricing the purchase runs; nothing is written and no code use ' +
        'is reserved.',
    request: {
        body: { content: { 'application/json': { schema: StationQuoteSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleQuoteSchema, 'The pass or cart priced, with its promotions and code.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such pass or product, or no such discount code.', 'not-found', 'discount-code-unknown'),
        409: problemResponse('The pass is not sold here, a product is unavailable, or the code does not apply.', 'conflict', 'discount-code-inactive', 'discount-code-expired', 'discount-code-minimum', 'discount-limit-reached'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const checkStationDiscountCodeRoute = createRoute({
    method: 'post',
    path: '/v1/station/discount-codes/check',
    operationId: 'checkStationDiscountCode',
    tags: ['commerce', 'stations'],
    summary: 'Check a discount code at the kiosk (F-POS-02)',
    description: `${STATION_CODES_AUTH} Answers what the code takes off \`amount\` and the new total, or why it does not apply ` +
        '(unknown, not active, expired, below its minimum, used up). Nothing is written and no use is reserved: the ' +
        'code is applied for real on the sale that pays — `discountCode` on a kiosk pass purchase or food order.',
    request: {
        body: { content: { 'application/json': { schema: CheckStationDiscountCodeSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationDiscountQuoteSchema, 'The discount and the new total.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such discount code.', 'discount-code-unknown'),
        409: problemResponse('The code does not apply to this amount.', 'discount-code-inactive', 'discount-code-expired', 'discount-code-minimum', 'discount-limit-reached', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const redeemStationCouponRoute = createRoute({
    method: 'post',
    path: '/v1/station/coupons/redeem',
    operationId: 'redeemStationCoupon',
    tags: ['commerce', 'stations'],
    summary: 'Redeem a coupon at the kiosk (F-POS-06)',
    description: `${STATION_CODES_AUTH} Redeems the coupon for the registered player on the station's active session — the ` +
        'same atomic redemption, ledger grant and audit as the console’s `redeemCoupon`. Refused when nobody with a ' +
        'registered account is in session here. Rate limited to 10 failed attempts per player per hour (spec/06 §8.5). ' +
        'Audited.',
    request: {
        body: { content: { 'application/json': { schema: RedeemStationCouponSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(CouponRedemptionSchema, 'What the redemption granted.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such coupon.', 'coupon-unknown'),
        409: problemResponse('The coupon grants nothing to this player here.', 'coupon-no-player', 'coupon-inactive', 'coupon-expired', 'coupon-used-up', 'coupon-already-redeemed', 'coupon-not-valid-here', 'conflict'),
        429: problemResponse('Too many failed redemption attempts; try later.', 'rate-limited'),
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-codes.js.map